import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React, { useState } from 'react';
import type { IChangeTaskPhaseConfirmationParameters } from '../models/ConfirmationModalParameters';
import { changeTaskEstimation, changeTaskReleaseEstimation } from '../action-creators/tasks';

interface ConfirmTaskPhaseChangeDialogProps {
  isOpen: boolean;
  modalParameters?: IChangeTaskPhaseConfirmationParameters;
  onClose?: () => void;
  onConfirm?: (result: number) => void;
  onCancel?: () => void;
}

const ConfirmTaskPhaseChangeDialog: React.FC<ConfirmTaskPhaseChangeDialogProps> = ({
  isOpen,
  modalParameters,
  onClose,
  onConfirm,
  onCancel,
}) => {
  const [newEstimate, setNewEstimate] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleInputChange = (value: string) => {
    let estimateValue: number | null = null;

    if (value) {
      estimateValue = Number(value);
    }

    setNewEstimate(estimateValue);

    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  const handleDismiss = (callback?: () => void) => {
    setNewEstimate(null);
    if (callback) callback();
  };

  const handeConfirm = async () => {
    if (!modalParameters?.taskId || !newEstimate || newEstimate <= 0) {
      setErrorMessage(modalParameters?.validationErrorMessage ?? null);
      return;
    }

    let result: { success: any; error?: string | undefined };
    if (modalParameters.isReleaseEstimation)
      result = await changeTaskReleaseEstimation({
        taskId: modalParameters.taskId,
        taskEstimateTimeInRelease: newEstimate,
      });
    else
      result = await changeTaskEstimation({
        taskId: modalParameters.taskId,
        taskEstimateTime: newEstimate,
      });

    if (result.success) {
      return handleDismiss(() => onConfirm?.(newEstimate));
    } else {
      return handleDismiss(onCancel);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth disableScrollLock>
        <DialogTitle>{modalParameters?.headerPlaceholder ?? 'Change task phase'}</DialogTitle>
        <DialogContent>
          <p>{modalParameters?.textPlaceholder}</p>
          <input
            min={0}
            type="number"
            className="dialog-textinput mb-3"
            autoComplete="off"
            value={newEstimate ?? ''}
            onChange={(e) => handleInputChange(e.target.value)}
          />
          <small
            role="alert"
            style={{
              color: 'red',
              fontSize: '0.875em',
              minHeight: '1.25em',
              display: 'block',
              textAlign: 'center',
              visibility: errorMessage ? 'visible' : 'hidden',
            }}
          >
            {errorMessage || '\u00a0'}
          </small>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDismiss(onClose)} variant="outlined">
            {modalParameters?.cancelButtonPlaceholer ?? 'Cancel'}
          </Button>
          <Button
            onClick={handeConfirm}
            variant="contained"
            color="primary"
            disabled={!!errorMessage}
          >
            {modalParameters?.confirmButtonPlaceholder ?? 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfirmTaskPhaseChangeDialog;
